import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { StudentListWrapper, View } from "@/components/StudentListWrapper";
import { useOrg } from "@/hooks/useOrg";
import { useFetchStudents } from "@/hooks/useFetchStudents";
import { FilterContext } from "@/types/FilterContext";
import {
  SortDirectionEnum,
  StudentListMostEngagedSortableFieldEnum,
} from "@/__generated__/types";
import { StudentOrdering } from "@/types/StudentOrdering";
import { useShareLink } from "@/hooks/useShareLink";
import {
  PaginationState,
  paginate,
  newState,
  getPaginationVars,
} from "@/util/pagination";

const PAGE_SIZE = 10; // table shows 10 rows per page

export const MostEngagedStudents = () => {
  const { t } = useTranslation();
  const { org } = useOrg();

  // Order by messages sent (descending) - using the new mostEngaged context
  const [ordering, setOrdering] = useState(
    new StudentOrdering(
      StudentListMostEngagedSortableFieldEnum.MessagesSent,
      SortDirectionEnum.Desc,
      "mostEngaged",
    ),
  );

  const [paginationState, setPaginationState] = useState<PaginationState>(
    newState(PAGE_SIZE),
  );

  const {
    items: students,
    endCursor,
    startCursor,
    loading,
    totalCount,
  } = useFetchStudents({
    paginationVariables: getPaginationVars(paginationState),
    context: new FilterContext("mostEngaged"),
    ordering,
  });

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPaginationState((prev) =>
        paginate(newPage, prev, startCursor, endCursor),
      );
    },
    [startCursor, endCursor],
  );

  const { shareCurrentLink } = useShareLink();
  return (
    <StudentListWrapper
      title={t("home.mostEngagedStudents.title")}
      description={t("home.mostEngagedStudents.subtitle")}
      students={students}
      view={View.TABLE}
      enableGridView={false}
      ordering={ordering}
      setOrdering={setOrdering}
      showMessages={true}
      page={paginationState.page}
      perPage={paginationState.perPage}
      onPageChange={handlePageChange}
      loading={loading || !org?.id}
      totalCount={totalCount || 0}
      onShareClick={shareCurrentLink}
    />
  );
};
